import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { preloadGameSounds, initSoundSystem } from '@/utils/sound-utils';
import { SoundCategory } from '@/services/mobile-audio';
import appSound from '@/services/app-sound-service';

/**
 * AudioInitializer Component
 * 
 * Initializes the audio system when the app starts.
 * Preloads essential sounds and sets up event listeners.
 * This component doesn't render anything - it just performs initialization logic.
 */
export function AudioInitializer() {
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();
    
    // Initialize audio system
    const initAudio = async () => {
      try {
        console.log('Initializing audio system...');
        
        // Initialize sound system
        initSoundSystem();
        
        // Initialize app sounds
        appSound.initializeAppSounds();
        
        // Preload UI sounds for immediate use
        // These are most likely to be used right after app start
        await preloadGameSounds([SoundCategory.UI]);
        
        console.log('UI sounds preloaded successfully');
        
        // In the background, preload other sound categories
        preloadGameSounds([
          SoundCategory.Gameplay, 
          SoundCategory.Win, 
          SoundCategory.Lose,
          SoundCategory.Background
        ]).then(() => {
          console.log('All game sounds preloaded successfully');
        }).catch(error => {
          console.warn('Error preloading all sound categories:', error);
        });
        
        // Set up user interaction detection for web audio autoplay
        if (!isNative) {
          const userInteractionEvents = ['click', 'touchstart', 'keydown'];
          
          const markUserInteraction = () => {
            document.documentElement.setAttribute('data-user-interacted', 'true');
            // Remove event listeners after first interaction
            userInteractionEvents.forEach(event => {
              document.removeEventListener(event, markUserInteraction);
            });
            console.log('User interaction detected, audio playback enabled');
          };
          
          // Add event listeners to detect user interaction
          userInteractionEvents.forEach(event => {
            document.addEventListener(event, markUserInteraction, { once: true });
          });
        }
        
      } catch (error) {
        console.error('Failed to initialize audio system:', error);
      }
    };
    
    // Initialize audio
    initAudio();
    
    // Cleanup function
    return () => {
      // Nothing to clean up here
    };
  }, []);
  
  // This component doesn't render anything
  return null;
}

export default AudioInitializer;